// PloyToast — the app-wide toast surface, matching the Ploy Design System Figma
// (node 380:1327): a white card (12px radius, elevated shadow) with a filled
// status glyph, header + optional description, an optional text CTA + a close
// X, and a thin azurite countdown bar that drains over the toast's duration.
// Wired as the global toast `render` in main.tsx (ChakraProvider toastOptions).

import { Box, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ReactNode } from "react";
import { TbCheck, TbX } from "react-icons/tb";

type ToastStatus = "success" | "error" | "warning" | "info" | "loading";

// Filled status circle: brand-tone fill + a white glyph (check / x / ! / i).
const GLYPH: Record<ToastStatus, { bg: string; node: ReactNode }> = {
  success: { bg: "success.500", node: <Icon as={TbCheck} boxSize="11px" strokeWidth={3} /> },
  error: { bg: "error.500", node: <Icon as={TbX} boxSize="11px" strokeWidth={3} /> },
  warning: { bg: "warning.500", node: <Box as="span" fontSize="11px" fontWeight={700} lineHeight="1">!</Box> },
  info: { bg: "p.500", node: <Box as="span" fontSize="11px" fontWeight={700} lineHeight="1">i</Box> },
  loading: { bg: "transparent", node: null },
};

// Azurite countdown bar drains left→right over the toast's lifetime.
const drain = keyframes`from { transform: scaleX(1); } to { transform: scaleX(0); }`;

export interface PloyToastAction {
  label: string;
  onClick: () => void;
}

export interface PloyToastProps {
  status?: ToastStatus;
  title?: ReactNode;
  description?: ReactNode;
  /** Optional trailing text CTA (e.g. "Undo"). */
  action?: PloyToastAction;
  /** Toast lifetime in ms — drives the countdown bar. */
  duration?: number | null;
  onClose?: () => void;
}

const PloyToast = ({
  status = "info",
  title,
  description,
  action,
  duration,
  onClose,
}: PloyToastProps) => {
  const g = GLYPH[status] ?? GLYPH.info;
  const timer = typeof duration === "number" && duration > 0 ? duration : null;

  return (
    <Box
      w="411px"
      maxW="calc(100vw - 32px)"
      bg="white"
      borderRadius="component.2xl" // 12px
      boxShadow="elevHigh"
      overflow="hidden"
      sx={{ WebkitFontSmoothing: "antialiased" }}
    >
      {/* body row */}
      <Flex gap="md" align="flex-start" px="lg" py="14px">
        {/* status glyph */}
        <Flex align="center" pt="1px" flexShrink={0}>
          {status === "loading" ? (
            <Spinner size="sm" color="p.500" />
          ) : (
            <Flex
              align="center"
              justify="center"
              w="16px"
              h="16px"
              borderRadius="full"
              bg={g.bg}
              color="white"
            >
              {g.node}
            </Flex>
          )}
        </Flex>

        {/* content */}
        <Flex flex="1" minW={0} direction="column" gap="sm" justify="center">
          <Box>
            {title && (
              <Text
                fontSize="md"
                fontWeight={600}
                color="text.body.primary"
                lineHeight="1"
                sx={{ wordBreak: "break-word" }}
              >
                {title}
              </Text>
            )}
            {description && (
              <Text
                fontSize="md"
                fontWeight={400}
                color="text.body.primary"
                lineHeight="1.5"
                mt="2xs"
                sx={{ wordBreak: "break-word" }}
              >
                {description}
              </Text>
            )}
          </Box>

          {action && (
            <Box
              as="button"
              type="button"
              onClick={action.onClick}
              alignSelf="flex-start"
              fontSize="md"
              fontWeight={600}
              color="text.body.tertiary"
              _hover={{ color: "text.body.primary" }}
            >
              {action.label}
            </Box>
          )}
        </Flex>

        {/* close */}
        {onClose && (
          <Box
            as="button"
            type="button"
            aria-label="Dismiss"
            onClick={onClose}
            flexShrink={0}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            p="2xs"
            borderRadius="component.md"
            color="neutral.600"
            transition="background-color 120ms, color 120ms"
            _hover={{ bg: "neutral.100", color: "neutral.900" }}
            _active={{ transform: "scale(0.96)" }}
          >
            <Icon as={TbX} boxSize="16px" />
          </Box>
        )}
      </Flex>

      {/* countdown bar */}
      <Box h="4px" w="full" bg="neutral.200" overflow="hidden">
        <Box
          h="full"
          w="full"
          bg="p.500"
          transformOrigin="left center"
          transform={timer ? undefined : "scaleX(1)"}
          sx={timer ? { animation: `${drain} ${timer}ms linear forwards` } : undefined}
        />
      </Box>
    </Box>
  );
};

export default PloyToast;
